import axios from 'axios'
import store from '@/store'
import { Toast, Dialog } from 'vant'
import router from '@/router'
import { removeToken } from '@/utils/auth'
import {
    isCacheableRequest,
    isCacheablePayload,
    getCacheKey,
    getCachePolicy,
    getCacheGeneration,
    readApiCache,
    writeApiCache,
    clearApiCache,
    markApiCacheStale,
} from '@/utils/apiCache'

const service = axios.create({
    baseURL: process.env.VUE_APP_BASE_API,
    timeout: 10000
})

const httpAdapter = axios.getAdapter(axios.defaults.adapter)
const inflightGets = new Map()

function bindCacheRevalidate() {
    if (typeof document === 'undefined' || bindCacheRevalidate.bound) return
    bindCacheRevalidate.bound = true

    const refreshOnResume = () => {
        markApiCacheStale()
        const silent = { skipNetworkToast: true }
        store.dispatch('config/refreshWebViewConfigIfNeeded', silent).catch(() => {})
        if (!store.state.user.token) return
        store.dispatch('user/getUserInfo', silent).catch(() => {})
        store.dispatch('notification/fetchNotifications', silent).catch(() => {})
    }

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') refreshOnResume()
    })
    window.addEventListener('pageshow', (event) => {
        if (event.persisted) refreshOnResume()
    })
}

bindCacheRevalidate()

function shareInflightGet(config, cacheKey) {
    const existing = inflightGets.get(cacheKey)
    if (existing) {
        config.fromApiCache = true
        config.apiCacheFollower = true
        config.adapter = () => existing.then((response) => ({
            data: response.data,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
            config,
            request: null,
        }))
        return
    }

    let resolveShared
    let rejectShared
    const shared = new Promise((resolve, reject) => {
        resolveShared = resolve
        rejectShared = reject
    })
    inflightGets.set(cacheKey, shared)

    config.adapter = (cfg) => httpAdapter(cfg).then((response) => {
        resolveShared(response)
        inflightGets.delete(cacheKey)
        return response
    }).catch((error) => {
        inflightGets.delete(cacheKey)
        rejectShared(error)
        return Promise.reject(error)
    })
}

service.interceptors.request.use(
    config => {
        if (store.state.user.token) {
            config.headers['Authorization'] = `Bearer ${store.state.user.token}`
        }

        if (isCacheableRequest(config)) {
            const cacheKey = getCacheKey(config, store.state.user.token)
            const policy = getCachePolicy(config)
            const cachedData = readApiCache(cacheKey)
            config.apiCacheKey = cacheKey
            config.apiCachePolicy = policy
            config.apiCacheGeneration = getCacheGeneration()

            // 只有刚拿到的结果才直接复用；过期后必须重新请求
            if (cachedData !== null) {
                config.adapter = () => Promise.resolve({
                    data: cachedData,
                    status: 200,
                    statusText: 'OK',
                    headers: {},
                    config,
                    request: null
                })
                config.fromApiCache = true
            } else if (policy.fresh > 0) {
                shareInflightGet(config, cacheKey)
            }
        }

        return config
    },
    error => {
        return Promise.reject(error)
    }
)

// 标记是否正在重定向到登录页面，防止多次重定向
let isRedirecting = false
service.interceptors.response.use(
    response => {
        const method = (response.config?.method || 'get').toLowerCase()

        if (response.config?.apiCacheKey && !response.config?.fromApiCache) {
            const policy = response.config.apiCachePolicy || { fresh: 0, stale: 0 }
            const stillCurrent = response.config.apiCacheGeneration === getCacheGeneration()
            if (stillCurrent && isCacheablePayload(response.data)) {
                writeApiCache(
                    response.config.apiCacheKey,
                    response.data,
                    policy.fresh,
                    policy.stale
                )
            }
        } else if (method !== 'get') {
            clearApiCache()
        }

        return response.data
    },
    error => {
        // 并发里跟上的请求不重复弹窗，由发起的那一次处理
        if (error.config?.apiCacheFollower) {
            if (error.config?.apiCacheKey && !error.response) {
                const staleData = readApiCache(error.config.apiCacheKey, true)
                if (staleData !== null) return Promise.resolve(staleData)
            }
            return Promise.reject(error)
        }

        // 网络失败时短时间回退旧数据；太旧的结果直接当作失败
        if (error.config?.apiCacheKey && !error.response) {
            const staleData = readApiCache(error.config.apiCacheKey, true)
            if (staleData !== null) {
                return Promise.resolve(staleData)
            }
        }

        // 确保错误对象和错误响应存在
        if (error.response) {
            const status = error.response.status;

            // 解决在default中使用let的ESLint错误
            let errorMsg = '请求失败，请稍后再试';
            try {
                if (error.response.data && typeof error.response.data.msg === 'string') {
                    errorMsg = error.response.data.msg;
                }
            } catch (e) {
                console.error('处理错误消息时出错', e);
            }

            switch (status) {
                case 401:
                    // 防止多个请求同时触发多次跳转
                    if (!isRedirecting) {
                        isRedirecting = true

                        // 显示提示信息
                        Toast.fail('登录已过期，请重新登录')

                        clearApiCache()

                        // 清除用户状态
                        store.dispatch('user/logout').then(() => {
                            // 移除token
                            removeToken()

                            // 记录当前路径，以便登录后可以返回
                            const currentPath = router.currentRoute.value.fullPath

                            // 跳转到登录页面，并带上原目标路径
                            router.push({
                                path: '/login',
                                query: currentPath !== '/login' ? { redirect: currentPath } : {}
                            }).finally(() => {
                                // 重置重定向标志
                                setTimeout(() => {
                                    isRedirecting = false
                                }, 1000)
                            })
                        })
                    }
                    break;

                case 403:
                    // 权限不足
                    Toast.fail('您没有权限执行此操作')
                    router.push('/403')
                    break;

                case 500:
                    Toast.fail('服务器错误，请稍后再试')
                    break;

                default:
                    // 业务页自行处理错误提示时，跳过全局弹窗
                    if (error.config?.skipErrorDialog) {
                        break;
                    }
                    Dialog.alert({
                        message: errorMsg
                    }).catch(() => {});
            }
        } else if (!error.config?.skipNetworkToast) {
            Toast.fail('网络连接失败，请检查您的网络');
        }

        return Promise.reject(error)
    }
)

export default service
export { clearApiCache }
